import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, switchMap, tap, exhaustMap, filter } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { HeadProductClient, ProductClient, ProductDto } from '@api/api-service';
import { HeadProductDetailsActions } from './head-product-details.actions';
import { MessageService } from 'primeng/api';

@Injectable()
export class HeadProductDetailsEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private headProductClient = inject(HeadProductClient);
    private productClient = inject(ProductClient);
    private messageService = inject(MessageService);

    //******************************************************************************************************************* */
    //*********************************************** LOAD HEAD PRODUCT BY ID ******************************************* */
    //******************************************************************************************************************* */

    GetHeadProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeadProductDetailsActions.getHeadProduct),
            switchMap(({ id }) =>
                this.headProductClient.getHeadProductByIdPublic(id).pipe(
                    map(response => HeadProductDetailsActions.getHeadProductSuccess({ response })),
                    catchError(error =>
                        of(HeadProductDetailsActions.getHeadProductFailure({ error: error.message || 'Failed to load head product' })),
                    ),
                ),
            ),
        ),
    );

    //******************************************************************************************************************* */
    //*********************************************** OPEN PRODUCT FROM VARIANT ***************************************** */
    //******************************************************************************************************************* */

    // TODO: Add headProductId to BasketProductDto and ShoppingListProductDto, then replace the
    // product buttons in basket and shopping list with <a routerLink> (supports ctrl+click / new tab)
    // and remove this effect. headProductId is missing today, so the product is looked up on click.
    OpenProductFromVariant$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(HeadProductDetailsActions.openProductFromVariant),
                exhaustMap(({ productId }) =>
                    this.productClient.getById(productId).pipe(
                        tap(product => {
                            if (!product.headProductId) return;
                            this.router.navigate(['/head-product', product.headProductId], {
                                queryParams: { variant: productId },
                            });
                        }),
                        catchError(() => EMPTY),
                    ),
                ),
            ),
        { dispatch: false },
    );

    //******************************************************************************************************************* */
    //*********************************************** PRESELECT VARIANT ************************************************* */
    //******************************************************************************************************************* */

    PreselectVariant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeadProductDetailsActions.getHeadProductSuccess),
            map(({ response }) => {
                const variantId = Number(this.router.routerState.snapshot.root.queryParamMap.get('variant'));
                return response.products?.find(product => product.id === variantId);
            }),
            filter((variant): variant is ProductDto => !!variant),
            switchMap(variant =>
                of(
                    HeadProductDetailsActions.setSelectedDimension({ dimension: variant.dimension ?? null }),
                    HeadProductDetailsActions.setSelectedOption1({ option1: variant.option_1 ?? null }),
                    HeadProductDetailsActions.setSelectedOption2({ option2: variant.option_2 ?? null }),
                    HeadProductDetailsActions.setSelectedOption3({ option3: variant.option_3 ?? null }),
                ),
            ),
        ),
    );

    //******************************************************************************************************************* */
    //*********************************************** MESSAGE SERVICE *************************************************** */
    //******************************************************************************************************************* */

    // Error messages
    MESSAGE_SERVICE_Error$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(HeadProductDetailsActions.getHeadProductFailure),
                tap(({ error }) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Fel',
                        detail: error,
                    });
                }),
            ),
        { dispatch: false },
    );
}
